import { Injectable } from '@angular/core';
import { SharedService } from 'src/app/layouts/shared-service';
import {
    localStorageAccessConstant,
    LocalStorageKey,
    LocalStorageValueType,
    UserContext
} from 'src/app/constants/LocalStorageAccessConstant';

@Injectable({
    providedIn: 'root'
})
export class UserContextService {
    constructor(private sharedService: SharedService) {}

    /**
     * Gets complete user context with all properties
     * Type-safe - no raw strings allowed, uses constant keys only
     */
    getCompleteContext(): UserContext {
        return {
            icao: this.getString(localStorageAccessConstant.icao),
            fbo: this.getString(localStorageAccessConstant.fbo),
            fboId: this.getNumber(localStorageAccessConstant.fboId),
            conductorFbo: this.getBoolean(localStorageAccessConstant.conductorFbo),
            groupId: this.getNumber(localStorageAccessConstant.groupId),
            impersonatedrole: this.getString(localStorageAccessConstant.impersonatedrole),
            managerGroupId: this.getNumber(localStorageAccessConstant.managerGroupId),
            favoriteAircrafts: this.getAny(localStorageAccessConstant.favoriteAircrafts),
            accountType: this.getNumber(localStorageAccessConstant.accountType),
            dismissedFavoriteAircrafts: this.getAny(localStorageAccessConstant.dismissedFavoriteAircrafts),
            notifiedFavoriteAircraft: this.getAny(localStorageAccessConstant.notifiedFavoriteAircraft),
            isSingleSourceFbo: this.getBoolean(localStorageAccessConstant.isSingleSourceFbo),
            isNetworkFbo: this.getBoolean(localStorageAccessConstant.isNetworkFbo),
            currentUser: this.getAny(localStorageAccessConstant.currentUser),
            decimalPrecision: this.getNumber(localStorageAccessConstant.decimalPrecision),
            isJetNetIntegrationEnabled: this.getBoolean(localStorageAccessConstant.isJetNetIntegrationEnabled),
            refreshToken: this.getString(localStorageAccessConstant.refreshToken),
            isNewFbo: this.getBoolean(localStorageAccessConstant.isNewFbo),
            dashboardSettings: this.getAny(localStorageAccessConstant.dashboardSettings),
            analyticsSettings: this.getAny(localStorageAccessConstant.analyticsSettings),
            permitedRoles: this.getAny(localStorageAccessConstant.permitedRoles),
            isFboSelected: this.getBoolean(localStorageAccessConstant.isFboSelected),
        };
    }

    /**
     * Gets a subset of user context - type-safe keys only
     * Usage: getContextSubset([localStorageAccessConstant.icao, localStorageAccessConstant.fboId])
     * @param keys - Array of constant keys (not raw strings)
     * @returns Subset of UserContext with only requested properties
     */
    getContextSubset<K extends LocalStorageKey>(
        keys: K[]
    ): Pick<UserContext, K> {
        const context = {} as Pick<UserContext, K>;
        
        for (const key of keys) {
            context[key] = this.getProperty(key) as UserContext[K];
        }
        
        return context;
    }

    /**
     * Gets a single property - type-safe, no raw strings
     * Usage: getProperty(localStorageAccessConstant.icao)
     * @param key - Constant key from localStorageAccessConstant (not raw string)
     * @returns Typed value based on LocalStorageValueType
     */
    getProperty<K extends LocalStorageKey>(key: K): LocalStorageValueType[K] {
        const storageKey = localStorageAccessConstant[key];
        const value = this.sharedService.getCurrentUserPropertyValue(storageKey);
        
        // Type-based conversion
        return this.convertValue(value, key) as LocalStorageValueType[K];
    }

    /**
     * Gets a string property value
     * Usage: getString(localStorageAccessConstant.icao)
     * @param key - Constant key (not raw string)
     */
    getString(key: typeof localStorageAccessConstant[LocalStorageKey]): string | null {
        return this.sharedService.getCurrentUserPropertyValue<string>(key);
    }

    /**
     * Gets a number property value
     * Usage: getNumber(localStorageAccessConstant.fboId)
     * @param key - Constant key (not raw string)
     */
    getNumber(key: typeof localStorageAccessConstant[LocalStorageKey]): number | null {
        const value = this.sharedService.getCurrentUserPropertyValue<string>(key);
        return value ? Number(value) : null;
    }

    /**
     * Gets a boolean property value
     * Usage: getBoolean(localStorageAccessConstant.isNetworkFbo)
     * @param key - Constant key (not raw string)
     */
    getBoolean(key: typeof localStorageAccessConstant[LocalStorageKey]): boolean | null {
        const value = this.sharedService.getCurrentUserPropertyValue<boolean>(key);
        return value !== null && value !== undefined ? Boolean(value) : null;
    }

    /**
     * Gets any type property value (for complex objects)
     * Usage: getAny(localStorageAccessConstant.dashboardSettings)
     * @param key - Constant key (not raw string)
     */
    getAny(key: typeof localStorageAccessConstant[LocalStorageKey]): any | null {
        return this.sharedService.getCurrentUserPropertyValue<any>(key);
    }

    /**
     * Converts value based on the key's expected type from LocalStorageValueType
     */
    private convertValue(value: any, key: LocalStorageKey): any {
        if (value === null || value === undefined) {
            return null;
        }

        // Type conversion based on LocalStorageValueType
        const typeMap: Record<LocalStorageKey, 'string' | 'number' | 'boolean' | 'any'> = {
            icao: 'string',
            fbo: 'string',
            fboId: 'number',
            conductorFbo: 'boolean',
            groupId: 'number',
            impersonatedrole: 'string',
            managerGroupId: 'number',
            favoriteAircrafts: 'any',
            accountType: 'number',
            dismissedFavoriteAircrafts: 'any',
            notifiedFavoriteAircraft: 'any',
            isSingleSourceFbo: 'boolean',
            isNetworkFbo: 'boolean',
            currentUser: 'any',
            decimalPrecision: 'number',
            isJetNetIntegrationEnabled: 'boolean',
            refreshToken: 'string',
            isNewFbo: 'boolean',
            dashboardSettings: 'any',
            analyticsSettings: 'any',
            permitedRoles: 'any',
            isFboSelected: 'boolean',
        };

        const type = typeMap[key];
        
        switch (type) {
            case 'number':
                return Number(value);
            case 'boolean':
                return Boolean(value);
            case 'string':
                return String(value);
            default:
                return value;
        }
    }
}

